import { environment } from '../environment/environment';

export interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  title: string;
  is_active: boolean;
  invited_by: string | null;
  created_at: string;
  user_role: {
    role_hierarchy: number | null;
  };
}

export const fetchUsers = async (): Promise<UserData[]> => {
  const token = localStorage.getItem('access_token');
  if (!token) return [];

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          query GetUsers {
            app_user(order_by: { created_at: desc }) {
              id
              name
              email
              role
              department
              title
              is_active
              invited_by
              created_at
              user_role {
                role_hierarchy
              }
            }
          }
        `,
      }),
    });

    const result = await response.json();

    if (result.errors) {
      console.error('Failed to fetch users:', result.errors);
      return [];
    }

    return result.data.app_user || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

/** Map role to the next lower role for downgrade */
const ROLE_DOWNGRADE: Record<string, string> = {
  mod: 'bdo',
  bdo: 'customer',
};

export const downgradeUserRole = async (userId: string, currentRole: string): Promise<{ success: boolean; error?: string; newRole?: string }> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  const newRole = ROLE_DOWNGRADE[currentRole];
  if (!newRole) return { success: false, error: 'Cannot downgrade this role further' };

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation DowngradeUser($id: uuid!, $role: String!) {
            update_app_user_by_pk(pk_columns: { id: $id }, _set: { role: $role }) {
              id
              role
            }
          }
        `,
        variables: { id: userId, role: newRole },
      }),
    });

    const result = await response.json();
    if (result.errors) {
      console.error('Failed to downgrade user:', result.errors);
      return { success: false, error: 'Failed to downgrade user' };
    }
    if (!result.data?.update_app_user_by_pk) {
      console.error('Downgrade returned null — likely a Hasura permission issue', result);
      return { success: false, error: 'Permission denied. Check Hasura update permissions for the role column on app_user.' };
    }
    return { success: true, newRole };
  } catch (error) {
    console.error('Error downgrading user:', error);
    return { success: false, error: 'Network error' };
  }
};

export const removeUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation RemoveUser($id: uuid!) {
            delete_app_user_by_pk(id: $id) {
              id
            }
          }
        `,
        variables: { id: userId },
      }),
    });

    const result = await response.json();
    if (result.errors) {
      console.error('Failed to remove user:', result.errors);
      return { success: false, error: 'Failed to remove user' };
    }
    return { success: true };
  } catch (error) {
    console.error('Error removing user:', error);
    return { success: false, error: 'Network error' };
  }
};

interface InviteUserInput {
  email: string;
  name: string;
  role: string;
  department?: string;
  title?: string;
  invited_by: string;
}

interface InviteUserResult {
  success: boolean;
  error?: string;
}

export const inviteUser = async (input: InviteUserInput): Promise<InviteUserResult> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation InviteUser($email: String!, $name: String!, $role: String!, $department: String, $title: String, $invited_by: uuid!) {
            insert_app_user_one(object: {
              email: $email,
              name: $name,
              role: $role,
              department: $department,
              title: $title,
              invited_by: $invited_by,
              is_active: false
            }) {
              id
            }
          }
        `,
        variables: {
          email: input.email,
          name: input.name,
          role: input.role,
          department: input.department || null,
          title: input.title || null,
          invited_by: input.invited_by,
        },
      }),
    });

    const result = await response.json();

    if (result.errors) {
      const errorMsg = result.errors[0]?.message || '';
      console.error('Failed to invite user:', result.errors);
      if (errorMsg.includes('Uniqueness violation') || errorMsg.includes('unique constraint')) {
        return { success: false, error: 'A user with this email already exists.' };
      }
      return { success: false, error: 'Something went wrong. Please try again.' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error inviting user:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};
